import { useState } from 'react'
import { supabase } from '../lib/supabase'
import { useI18n } from '../i18n/useI18n'
import { formatDate, formatMoney } from '../lib/format'
import { DocumentLink } from '../components/DocumentViewer'
import { Sheet } from '../components/Sheet'
import { TextAreaField } from '../components/Fields'
import { ConfirmDelete } from '../components/ConfirmDelete'
import type { Expense } from '../lib/types'

type CatKey =
  | 'cat_plomberie' | 'cat_vidange' | 'cat_eau' | 'cat_electricite'
  | 'cat_peinture' | 'cat_amende' | 'cat_menage' | 'cat_autre'

/**
 * Decision du proprietaire sur une depense.
 *
 * L'approbation sans facture est impossible : la contrainte
 * expenses_approbation_complete de schema.sql la refuse. Le bouton est
 * donc desactive, avec l'explication, plutot que de laisser partir une
 * requete vouee a echouer.
 */
export function ApprovalSheet({
  expense,
  unitName,
  onClose,
  onSaved,
}: {
  expense: Expense
  unitName: string | null
  onClose: () => void
  onSaved: () => void
}) {
  const { t } = useI18n()
  const [reason, setReason] = useState(expense.decision_reason ?? '')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const sansFacture = expense.invoice_document === null

  async function decide(status: 'approuve' | 'refuse') {
    if (status === 'refuse' && reason.trim().length === 0) return
    setError(null)
    setSubmitting(true)

    const { data: userData } = await supabase.auth.getUser()
    const { error: e } = await supabase
      .from('expenses')
      .update({
        status,
        approved_by: userData.user?.id ?? null,
        approved_at: new Date().toISOString(),
        decision_reason: reason.trim() || null,
      })
      .eq('id', expense.id)

    setSubmitting(false)
    if (e) {
      setError(`${t('save_failed')} ${e.message}`)
      return
    }
    onSaved()
  }

  return (
    <Sheet
      title={t('approvals_title')}
      onClose={onClose}
      onSubmit={() => void decide('approuve')}
      submitLabel={t('approve')}
      submitting={submitting}
      disabled={sansFacture}
      error={error}
    >
      <dl className="deflist">
        <dt>{t('expense_category')}</dt>
        <dd>{t(`cat_${expense.category}` as CatKey)}</dd>
        <dt>{t('expense_amount')}</dt>
        <dd>
          <strong>{formatMoney(expense.amount)}</strong>
        </dd>
        <dt>{t('expense_date')}</dt>
        <dd>{formatDate(expense.spent_on)}</dd>
        <dt>{t('expense_unit')}</dt>
        <dd>{unitName ?? t('expense_whole_building')}</dd>
        <dt>{t('expense_paid_by')}</dt>
        <dd>
          {t(`payer_${expense.paid_by}` as
            'payer_gerant' | 'payer_assistant' | 'payer_proprietaire'
            | 'payer_mere' | 'payer_locataire' | 'payer_autre')}
        </dd>
        {expense.supplier && (
          <>
            <dt>{t('expense_supplier')}</dt>
            <dd>{expense.supplier}</dd>
          </>
        )}
      </dl>

      {expense.description && <p className="muted">{expense.description}</p>}

      {/* La facture, consultable avant de decider. */}
      <DocumentLink
        documentId={expense.invoice_document}
        label={t('expense_invoice')}
      />

      {expense.tenant_charge && (
        <div className="banner banner--warn">
          <strong>{t('tenant_charge_flag')}</strong>
          <span>{t('tenant_charge_detected')}</span>
        </div>
      )}

      {sansFacture && (
        <div className="banner banner--error">
          <strong>{t('approve_needs_invoice')}</strong>
        </div>
      )}

      <TextAreaField
        label={t('reject_reason')}
        hint={t('reject_reason_hint')}
        value={reason}
        onChange={setReason}
      />

      <button
        type="button"
        className="btn btn--ghost"
        disabled={submitting || reason.trim().length === 0}
        onClick={() => void decide('refuse')}
      >
        {t('reject')}
      </button>

      {/* Suppression, reservee au proprietaire et aux depenses non
          approuvees. Une depense approuvee est entree dans le
          reglement du mois : le net a verser en depend, et la retirer
          changerait un chiffre deja communique. Le declencheur de
          db/009 le refuse aussi cote base. */}
      <ConfirmDelete
        label={t('expense_delete')}
        question={t('expense_delete_confirm', {
          amount: formatMoney(expense.amount),
        })}
        disabled={submitting}
        blockedReason={
          expense.status === 'approuve' ? t('expense_delete_blocked') : null
        }
        onDelete={async () => {
          const { error: e } = await supabase
            .from('expenses')
            .delete()
            .eq('id', expense.id)
          if (e) throw new Error(e.message)
          onSaved()
        }}
      />
    </Sheet>
  )
}
